import {
  cloneElement,
  createContext,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import { AnimatePresence, motion, useIsPresent } from "framer-motion";
import { Check, CheckCheck, CircleX, Loader2, MinusCircle } from "lucide-react";

import { getTaskStatusMessage } from "./utils";
import { Badge } from "@/modern/components/ui/badge";
import { cn } from "@/modern/utils";

import { useAsyncTasks } from "./hooks/useAsyncTasks";
import type { AsyncTask, AsyncTaskFilter, AsyncTaskStatus } from "./types";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface AsyncTaskUIEffects {
  onCompleted?: (task: AsyncTask) => void;
  onFailed?: (task: AsyncTask) => void;
  onCancelled?: (task: AsyncTask) => void;
}

export interface AsyncTaskUIProps {
  filter: AsyncTaskFilter;
  effects?: AsyncTaskUIEffects;
  children: ReactNode;
}

interface AsyncTaskUIContextValue {
  filteredTasks: AsyncTask[];
  pendingTasks: AsyncTask[];
  hasPending: boolean;
  taskMatching: (predicate: (task: AsyncTask) => boolean) => boolean;
}

// ── Context ───────────────────────────────────────────────────────────────────

const AsyncTaskUIContext = createContext<AsyncTaskUIContextValue | undefined>(
  undefined,
);

export function useAsyncTaskUIContext(): AsyncTaskUIContextValue {
  const ctx = useContext(AsyncTaskUIContext);
  if (!ctx) {
    throw new Error("useAsyncTaskUIContext must be used within AsyncTaskUI");
  }
  return ctx;
}

// ── Banner ────────────────────────────────────────────────────────────────────

/** Session-scoped dismiss memory (no localStorage); cleared on full page reload. */
const bannerDismissedTaskIds = new Set<number>();

const BANNER_MOTION_DURATION_S = 0.25;
const DEFAULT_BANNER_HOLD_MS = 2500;

export interface AsyncTaskUIBannerProps {
  className?: string;
  /** How long completed/failed/cancelled strips stay visible before exit animation */
  dismissDelayMs?: number;
}

const BANNER_STRIP_STYLES: Record<AsyncTaskStatus, string> = {
  Pending: "border-primary/30 border-l-primary bg-primary/[0.1]",
  Failed: "border-destructive/30 border-l-destructive bg-destructive/[0.08]",
  Completed:
    "border-emerald-500/30 border-l-emerald-500 bg-emerald-500/10",
  Resolved:
    "border-emerald-500/20 border-l-emerald-500/60 bg-emerald-500/[0.06]",
  Cancelled: "border-muted-foreground/30 border-l-muted-foreground bg-muted",
};

const BANNER_ICON_STYLES: Record<AsyncTaskStatus, string> = {
  Pending: "text-primary",
  Failed: "text-destructive",
  Completed: "text-emerald-600 dark:text-emerald-400",
  Resolved: "text-emerald-600/70 dark:text-emerald-400/70",
  Cancelled: "text-muted-foreground",
};

function BannerStatusIcon({ status }: { status: AsyncTaskStatus }) {
  const className = cn("h-4 w-4 shrink-0", BANNER_ICON_STYLES[status]);

  switch (status) {
    case "Pending":
      return <Loader2 className={cn(className, "animate-spin")} aria-hidden />;
    case "Failed":
      return <CircleX className={className} aria-hidden />;
    case "Completed":
      return <Check className={className} aria-hidden />;
    case "Resolved":
      return <CheckCheck className={className} aria-hidden />;
    case "Cancelled":
      return <MinusCircle className={className} aria-hidden />;
  }
}

function StatusStrip({ task }: { task: AsyncTask }) {
  const message = getTaskStatusMessage(task);
  const { status } = task;

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "shrink-0 flex items-center gap-2.5 px-4 py-3",
        "border rounded-md border-l-[3px] transition-colors duration-300",
        BANNER_STRIP_STYLES[status],
      )}
    >
      <BannerStatusIcon status={status} />
      <p
        className={cn(
          "text-sm leading-snug min-w-0",
          status === "Pending" || status === "Failed"
            ? "text-muted-foreground"
            : "font-medium text-foreground",
        )}
      >
        {message}
      </p>
    </div>
  );
}

function BannerMotionItem({
  task,
  onExited,
}: {
  task: AsyncTask;
  onExited: (taskId: number) => void;
}) {
  const isPresent = useIsPresent();

  useEffect(() => {
    return () => {
      if (!isPresent) {
        onExited(task.id);
      }
    };
  }, [isPresent, task.id, onExited]);

  return (
    <motion.div
      layout
      className="overflow-hidden"
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: BANNER_MOTION_DURATION_S }}
    >
      <StatusStrip task={task} />
    </motion.div>
  );
}

function AsyncTaskUIBanner({
  className,
  dismissDelayMs = DEFAULT_BANNER_HOLD_MS,
}: AsyncTaskUIBannerProps) {
  const { pendingTasks, filteredTasks } = useAsyncTaskUIContext();
  const [renderedTasks, setRenderedTasks] = useState<AsyncTask[]>([]);
  const [exitingCount, setExitingCount] = useState(0);
  const holdTimersRef = useRef(new Map<number, ReturnType<typeof setTimeout>>());
  const scheduledRemovalRef = useRef(new Set<number>());

  // Only pending (in-progress) tasks can enter the banner. Terminal states are
  // shown only as a follow-through for strips that were pending this session —
  // a reload with no pending work shows nothing.
  const eligiblePending = useMemo(
    () =>
      pendingTasks.filter((task) => !bannerDismissedTaskIds.has(task.id)),
    [pendingTasks],
  );

  const scheduleRemoval = useCallback(
    (taskId: number) => {
      if (scheduledRemovalRef.current.has(taskId)) return;
      scheduledRemovalRef.current.add(taskId);

      const existing = holdTimersRef.current.get(taskId);
      if (existing != null) clearTimeout(existing);

      holdTimersRef.current.set(
        taskId,
        setTimeout(() => {
          holdTimersRef.current.delete(taskId);
          scheduledRemovalRef.current.delete(taskId);
          setExitingCount((count) => count + 1);
          setRenderedTasks((prev) => prev.filter((task) => task.id !== taskId));
        }, dismissDelayMs),
      );
    },
    [dismissDelayMs],
  );

  // Sync pending tasks in and promote to terminal snapshot when they leave pending.
  useEffect(() => {
    const eligibleIds = new Set(eligiblePending.map((task) => task.id));

    setRenderedTasks((prev) => {
      const next = [...prev];

      for (const task of eligiblePending) {
        const index = next.findIndex((item) => item.id === task.id);
        if (index === -1) {
          next.push(task);
        } else {
          next[index] = task;
        }
      }

      for (const task of prev) {
        if (eligibleIds.has(task.id)) continue;

        const terminal = filteredTasks.find((item) => item.id === task.id);
        const index = next.findIndex((item) => item.id === task.id);
        if (index === -1) continue;

        if (!terminal) {
          // Left the surface filter (e.g. API meta shape changed) — drop stale pending strip.
          if (next[index].status === "Pending") {
            next.splice(index, 1);
          }
          continue;
        }

        if (terminal.status === "Pending") continue;

        next[index] = terminal;
      }

      return next;
    });
  }, [eligiblePending, filteredTasks]);

  // Schedule auto-dismiss for any terminal strip still on screen.
  useEffect(() => {
    for (const task of renderedTasks) {
      if (task.status === "Pending") continue;
      scheduleRemoval(task.id);
    }
  }, [renderedTasks, scheduleRemoval]);

  useEffect(() => {
    const timers = holdTimersRef.current;
    const scheduled = scheduledRemovalRef.current;
    return () => {
      for (const [taskId, timer] of timers.entries()) {
        clearTimeout(timer);
        scheduled.delete(taskId);
      }
      timers.clear();
    };
  }, []);

  const handleBannerExited = useCallback((taskId: number) => {
    bannerDismissedTaskIds.add(taskId);
    // Temporarily disabled: do not markSeen on banner dismiss — completed
    // account tasks should remain visible in the notification popup.
    setExitingCount((count) => Math.max(0, count - 1));
  }, []);

  const showBanner = renderedTasks.length > 0 || exitingCount > 0;
  if (!showBanner) return null;

  return (
    <div className={cn(className)}>
      <div className="max-h-36 overflow-y-auto space-y-2">
        <AnimatePresence mode="popLayout" initial={false}>
          {renderedTasks.map((task) => (
            <BannerMotionItem
              key={task.id}
              task={task}
              onExited={handleBannerExited}
            />
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
}

// ── Loader ────────────────────────────────────────────────────────────────────

export interface AsyncTaskUILoaderProps {
  children: ReactElement<{ task?: AsyncTask }>;
  /** When set, only pending tasks matching this predicate are rendered. */
  forTask?: (task: AsyncTask) => boolean;
}

function AsyncTaskUILoader({ children, forTask }: AsyncTaskUILoaderProps) {
  const { pendingTasks } = useAsyncTaskUIContext();
  const tasks = forTask
    ? pendingTasks.filter(forTask)
    : pendingTasks;

  if (tasks.length === 0) return null;

  return (
    <>
      {tasks.map((task) => {
        if (!isValidElement(children)) return null;
        return cloneElement(children, { key: task.id, task });
      })}
    </>
  );
}

// ── Badge ─────────────────────────────────────────────────────────────────────

export interface AsyncTaskUIBadgeProps {
  forTask: (task: AsyncTask) => boolean;
  /** Fallback when notification config has no message for the task status */
  label?: string;
  className?: string;
}

const STATUS_PRIORITY: Record<AsyncTaskStatus, number> = {
  Pending: 0,
  Failed: 1,
  Completed: 2,
  Cancelled: 3,
  // Resolved is a settled/historical variant of Completed — lowest priority so
  // an incoming Pending/Failed still dominates `pickMatchingTask`.
  Resolved: 4,
};

const STATUS_STYLES: Record<AsyncTaskStatus, string> = {
  Pending: "border-primary/40 bg-primary/10 text-primary",
  Failed: "border-destructive/40 bg-destructive/10 text-destructive",
  Completed:
    "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
  Resolved:
    "border-emerald-500/25 bg-emerald-500/[0.06] text-emerald-700/80 dark:text-emerald-400/80",
  Cancelled: "border-muted-foreground/30 bg-muted text-muted-foreground",
};

function pickMatchingTask(
  tasks: AsyncTask[],
  forTask: (task: AsyncTask) => boolean,
): AsyncTask | undefined {
  const matching = tasks.filter(forTask);
  if (matching.length === 0) return undefined;

  return [...matching].sort((a, b) => {
    const byStatus = STATUS_PRIORITY[a.status] - STATUS_PRIORITY[b.status];
    if (byStatus !== 0) return byStatus;
    return b.id - a.id;
  })[0];
}

function BadgeStatusIcon({ status }: { status: AsyncTaskStatus }) {
  switch (status) {
    case "Pending":
      return <Loader2 className="h-3 w-3 shrink-0 animate-spin" aria-hidden />;
    case "Failed":
      return <CircleX className="h-3 w-3 shrink-0" aria-hidden />;
    case "Completed":
      return <Check className="h-3 w-3 shrink-0" aria-hidden />;
    case "Resolved":
      return <CheckCheck className="h-3 w-3 shrink-0" aria-hidden />;
    case "Cancelled":
      return <MinusCircle className="h-3 w-3 shrink-0" aria-hidden />;
  }
}

function AsyncTaskUIBadge({
  forTask,
  label = "In progress",
  className,
}: AsyncTaskUIBadgeProps) {
  const { filteredTasks } = useAsyncTaskUIContext();
  const task = pickMatchingTask(filteredTasks, forTask);

  if (!task) return null;

  const displayLabel = getTaskStatusMessage(task) || label;

  return (
    <Badge
      variant="outline"
      className={cn(
        "gap-1 text-xs font-medium transition-colors duration-300",
        STATUS_STYLES[task.status],
        className,
      )}
    >
      <BadgeStatusIcon status={task.status} />
      <span className="truncate">{displayLabel}</span>
    </Badge>
  );
}

// ── Root ──────────────────────────────────────────────────────────────────────

function AsyncTaskUIRoot({ filter, effects, children }: AsyncTaskUIProps) {
  const { tasks, register } = useAsyncTasks();

  const filteredTasks = useMemo(
    () => tasks.filter(filter),
    [tasks, filter],
  );

  const pendingTasks = useMemo(
    () => filteredTasks.filter((task) => task.status === "Pending"),
    [filteredTasks],
  );

  const contextValue = useMemo(
    () => ({
      filteredTasks,
      pendingTasks,
      hasPending: pendingTasks.length > 0,
      taskMatching: (predicate: (task: AsyncTask) => boolean) =>
        pendingTasks.some(predicate),
    }),
    [filteredTasks, pendingTasks],
  );

  useEffect(() => {
    return register({
      filter,
      onCompleted: effects?.onCompleted,
      onFailed: effects?.onFailed,
      onCancelled: effects?.onCancelled,
    });
  }, [
    register,
    filter,
    effects?.onCompleted,
    effects?.onFailed,
    effects?.onCancelled,
  ]);

  return (
    <AsyncTaskUIContext.Provider value={contextValue}>
      {children}
    </AsyncTaskUIContext.Provider>
  );
}

const AsyncTaskUI = Object.assign(AsyncTaskUIRoot, {
  Banner: AsyncTaskUIBanner,
  Loader: AsyncTaskUILoader,
  Badge: AsyncTaskUIBadge,
});

export { AsyncTaskUI };
